import { motion } from "framer-motion";
import { useEffect, createElement, useState, useRef } from "react";
import { createPortal } from "react-dom";
import ReactPlayer from "react-player";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Config } from "../../../config/config";
import { BubbleAnimation } from "../../../components/bubble";
import "./project-popup-mobile.css";
import ReactMarkdown from "react-markdown";

const YoutubeSlide = ({ url, playing }) => (
  <ReactPlayer
    className="carousel-player-mobile"
    width="100%"
    height="100%"
    url={url.url}
    playing={playing}
    controls
    style={{ paddingBottom: "1%" }}
  />
);

export const ProjectPopupMobile = ({ isOpen, onClose, project }) => {
  const containerVariants = {
    open: {
      opacity: 1,
      transition: { duration: 0.2, ease: "easeOut" }
    },
    closed: {
      opacity: 0,
      transition: { duration: 0.2, ease: "easeIn" }
    },
  };

  const modalVariants = {
    open: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.3, ease: "easeOut" }
    },
    closed: {
      opacity: 0,
      scale: 0.9,
      y: 50,
      transition: { duration: 0.3, ease: "easeIn" }
    },
  };

  const customRenderItem = (item, props) =>
    createElement(item.type, { ...item.props, ...props });

  const getVideoThumb = (videoId) =>
    `https://img.youtube.com/vi/${videoId}/default.jpg`;

  const getVideoId = (url) =>
    url.substr("https://www.youtube.com/embed/".length, url.length);

  const customRenderThumb = (children) =>
    children.map((item) => {
      const videoId = getVideoId(item.props.url.url);
      return <img src={getVideoThumb(videoId)} alt={`Thumbnail for video ${videoId}`} />;
    });

  const [isPlaying, setIsPlaying] = useState(false);
  const [swipeStartY, setSwipeStartY] = useState(null);
  const [swipeEndY, setSwipeEndY] = useState(null);
  const popupRef = useRef(null);
  const closeButtonRef = useRef(null);

  const handleClose = () => {
    setIsPlaying(false);
    onClose();
  };

  const handleTouchStart = (e) => {
    setSwipeStartY(e.touches[0].clientY);
  };

  const handleTouchEnd = (e) => {
    setSwipeEndY(e.changedTouches[0].clientY);
    if (swipeStartY && swipeEndY && swipeEndY - swipeStartY > 100) {
      // Swipe down to close
      handleClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleClose();
    }
  };

  useEffect(() => {
    if (isOpen === false) {
      setIsPlaying(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      // Focus management
      if (closeButtonRef.current) {
        closeButtonRef.current.focus();
      }
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Don't render if no project or popup is not open
  if (!project || !isOpen) {
    return null;
  }

  const modalContent = (
    <motion.div
      ref={popupRef}
      className="popup-mobile fixed inset-0 bg-black bg-opacity-50 z-[9999] "
      initial="closed"
      animate={isOpen ? "open" : "closed"}
      variants={containerVariants}
      // onClick={handleClose}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      role="dialog"
      aria-modal="true"
      aria-labelledby="project-popup-title"
      aria-describedby="project-popup-description"
    >
      <div className="container-popup-mobile mx-auto w-full h-full overflow-y-auto">
        <motion.div
          className="bg-black rounded-lg p-4 w-full h-full flex flex-col fixed top-0"
          initial="closed"
          animate={isOpen ? "open" : "closed"}
          variants={modalVariants}
          // onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
        >
          <BubbleAnimation triggerOnce={false}>
            {project.brand ? (
              <img
                className="mx-auto mt-4"
                src={Config.api.mediaUrl + project.brand}
                alt={project.title}
                width={150}
                height={150}
              />
            ) : (
              <div className="mx-auto my-4 w-[150px] h-[150px] bg-gray-800 rounded-xl flex items-center justify-center mb-10">
                <span className="text-white text-sm">No logo</span>
              </div>
            )}
          </BubbleAnimation>

          <div id="project-popup-title" className="sr-only">
            {project.title}
          </div>

          {project.youtube_urls && project.youtube_urls.length > 0 ? (
            <Carousel
              showThumbs={false}
              showIndicators={false}
              thumbWidth={150}
              dynamicHeight={false}
              renderItem={customRenderItem}
              renderThumbs={customRenderThumb}
              className="carousel-custom-mobile"
            >
              {project.youtube_urls.map((video, index) => (
                <YoutubeSlide
                  key={"youtube-" + index + project.id}
                  url={video}
                  playing={isPlaying}
                  className="carousel-player-mobile"
                />
              ))}
            </Carousel>
          ) : (
            <div className="flex items-center justify-center h-64 text-white">
              <p>No videos available for this project</p>
            </div>
          )}

          <div id="project-popup-description" className="flex justify-center items-center project-desc-mobile flex-1 overflow-y-auto">
            <ReactMarkdown
              className="text-white mt-10 text-xl text-left w-full leading-tight flex-1"
              escapeHtml={false}
              components={{
                strong: ({ node, ...props }) => (
                  <span className="text-strong text-[15pt]" {...props} />
                ),
              }}
            >
              {project.description}
            </ReactMarkdown>
          </div>

          <button
            ref={closeButtonRef}
            className="absolute top-4 right-4 text-white text-2xl font-bold cursor-pointer bg-black bg-opacity-50 rounded-full w-8 h-8 flex items-center justify-center"
            onClick={handleClose}
            aria-label="Close popup"
          >
            &#10005;
          </button>

          <div className="close-button-container mt-8 mb-4">
          </div>
        </motion.div>
      </div>
    </motion.div>
  );

  return createPortal(modalContent, document.body);
};

export default ProjectPopupMobile;